import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import { httpRest } from '../services/http';
import { configHeadersApiRest } from '../services/commonService';
import axios from 'axios';

function logApiError(context: string, error: any) {
	const errorInfo = {
		context,
		status: error?.response?.status,
		statusText: error?.response?.statusText,
		data: error?.response?.data,
		url: error?.config?.url,
		params: error?.config?.params,
		message: error?.message,
		headers: error?.config?.headers,
		timestamp: new Date().toISOString(),
	};
	
	console.error(`[fleetManager] ${context} failed:`, errorInfo);
	
	// Additional debugging for common errors
	if (error?.response?.status === 404) {
		console.warn(
			`[fleetManager] ❌ 404 Not Found: The API endpoint does not exist. ` +
			`Check:\n` +
			`1. View/table name is correct (vc_fleet_manager_total_tasks_2)\n` +
			`2. Backend service is running and accessible\n` +
			`3. Base URL is correct: ${error?.config?.url}`
		);
	}
	if (error?.response?.status === 403 || error?.response?.status === 401) {
		console.warn(
			`[fleetManager] ⚠️ Authentication error (${error?.response?.status}): ` +
			`Check API headers and credentials`
		);
	}
	if (error?.response?.status >= 500) {
		console.error(
			`[fleetManager] 🔥 Server error (${error?.response?.status}): Backend service may be down`
		);
	}
	if (!error?.response) {
		console.error(
			`[fleetManager] 🌐 Network error: Unable to reach the API endpoint. ` +
			`Check base URL and network connectivity`
		);
	}
}

export interface Task {
	correlationId: string;
	vin: string;
	driver_name: string;
	brief: string;
	agent_name: string;
	coaching_priority: string;
	financial_impact: number;
	status: string;
	inserted_at: string;
	coaching_summary?: string;
	urgency_display?: string;
	snooze?: boolean;
	snoozed_date?: string;
	snoozed_on?: string;
	[key: string]: any;
}

export interface TaskCounts {
	fixToday: number;
	thisWeek: number;
	keepAnEye: number;
	codesFlagged: number;
}

export interface TaskFilterParams {
	Vin?: string;
	fleet_id?: string;
	organisation_id?: string;
	fleet_name?: string;
	start_date?: string;
	end_date?: string;
	status?: string;
	Agent_name?: string;
	/** CRITICAL | HIGH | LOW,MEDIUM */
	coaching_priority?: string;
}

export interface AIAnalysisResult {
	brief: string;
	vin: string;
	'Spotted On'?: string;
	urgency_label?: string;
	'What we Saw': string[];
	'Do This': string[];
	triggered_issues?: string[];
	problem_description?: string;
	flagged_routes?: string[];
	financial_impact?: number;
	coaching_priority?: string;
	agent_name?: string;
	driver_name?: string;
	status?: string;
	date?: string;
	AI_Analysis?: string[];
	AI_Action_plan?: string[];
	[key: string]: any;
}

export interface FleetManagerState {
	taskCounts: TaskCounts | null;
	taskCountsLoading: boolean;
	taskCountsError: string | null;
	tasks: Task[];
	tasksLoading: boolean;
	tasksError: string | null;
	lastTaskFilter: TaskFilterParams;
	aiAnalysis: AIAnalysisResult | null;
	aiAnalysisLoading: boolean;
	aiAnalysisError: string | null;
	agentsList: string[];
	agentsListLoading: boolean;
	agentsListError: string | null;
}

const initialFleetManagerState: FleetManagerState = {
		taskCounts: null,
		taskCountsLoading: false,
		taskCountsError: null,
		tasks: [],
		tasksLoading: false,
		tasksError: null,
		lastTaskFilter: {},
		aiAnalysis: null,
		aiAnalysisLoading: false,
		aiAnalysisError: null,
		agentsList: ['All Agents'],
		agentsListLoading: false,
		agentsListError: null,
	};

export const fleetManager = createModel<RootModel>()({
	state: { ...initialFleetManagerState } as FleetManagerState,

	reducers: {
		clearStore(): FleetManagerState {
			return { ...initialFleetManagerState };
		},
		setTaskCounts(state, taskCounts: TaskCounts | null): FleetManagerState {
			return { ...state, taskCounts, taskCountsLoading: false, taskCountsError: null };
		},
		setTaskCountsLoading(state, taskCountsLoading: boolean): FleetManagerState {
			return { ...state, taskCountsLoading };
		},
		setTaskCountsError(state, taskCountsError: string): FleetManagerState {
			return { ...state, taskCountsError, taskCountsLoading: false };
		},
		setTasksLoading(state, tasksLoading: boolean): FleetManagerState {
			return { ...state, tasksLoading };
		},
		setTasks(state, tasks: Task[]): FleetManagerState {
			return { ...state, tasks, tasksLoading: false, tasksError: null };
		},
		setTasksError(state, tasksError: string): FleetManagerState {
			// CRITICAL: Clear stale data when error occurs
			return { ...state, tasks: [], tasksError, tasksLoading: false };
		},
		setLastTaskFilter(state, lastTaskFilter: TaskFilterParams): FleetManagerState {
			return { ...state, lastTaskFilter };
		},
		patchTaskStatus(
			state,
			payload: { correlationId: string; status: string },
		): FleetManagerState {
			return {
				...state,
				tasks: state.tasks.map((t) =>
					t.correlationId === payload.correlationId
						? { ...t, status: payload.status }
						: t,
				),
			};
		},
		setAiAnalysisLoading(state, aiAnalysisLoading: boolean): FleetManagerState {
			return { ...state, aiAnalysisLoading };
		},
		setAiAnalysis(state, aiAnalysis: AIAnalysisResult | null): FleetManagerState {
			return { ...state, aiAnalysis, aiAnalysisLoading: false, aiAnalysisError: null };
		},
		setAiAnalysisError(state, aiAnalysisError: string): FleetManagerState {
			return { ...state, aiAnalysisError, aiAnalysisLoading: false };
		},
		clearAiAnalysis(state): FleetManagerState {
			return { ...state, aiAnalysis: null, aiAnalysisLoading: false, aiAnalysisError: null };
		},
		setAgentsList(state, agentsList: string[]): FleetManagerState {
			return { ...state, agentsList, agentsListLoading: false, agentsListError: null };
		},
		setAgentsListLoading(state, agentsListLoading: boolean): FleetManagerState {
			return { ...state, agentsListLoading };
		},
		setAgentsListError(state, agentsListError: string): FleetManagerState {
			return { ...state, agentsListError, agentsListLoading: false };
		},
	},

	effects: (dispatch) => ({
		async fetchTaskCountsAsync(params: {
			Vin: string;
			fleet_id: string;
			organisation_id: string;
			start_date?: string;
			end_date?: string;
			Agent_name?: string;
			status?: string;
		}) {
			dispatch.fleetManager.setTaskCountsLoading(true);
			try {
				const config = {
					...configHeadersApiRest(),
					params: {
						fleet_id: params.fleet_id,
						organisation_id: params.organisation_id,
						Vin: params.Vin,
						start_date: params.start_date,
						end_date: params.end_date,
						Agent_name:
							params.Agent_name && params.Agent_name !== 'All Agents'
								? params.Agent_name
								: 'All Agents',
						status: params.status || 'open',
					},
				};
				const { data } = await httpRest.get('vc_fleet_manager_tasks_counts_2', config);
				const row = Array.isArray(data) && data.length > 0 ? data[0] : null;

				if (!row) {
					dispatch.fleetManager.setTaskCounts(null);
					return;
				}

				// Case-insensitive lookup; accepts the first key that exists in the row
				const getField = (obj: Record<string, any>, ...keys: string[]): number => {
					for (const key of keys) {
						const foundKey = Object.keys(obj).find(
							(k) => k.toLowerCase() === key.toLowerCase(),
						);
						if (foundKey !== undefined) return Number(obj[foundKey]) || 0;
					}
					return 0;
				};

				const taskCounts: TaskCounts = {
					// Response: [{"VIN_COUNT":15,"HIGH":0,"CRITICAL":20,"MEDIUM_LOW":0}]
					fixToday: getField(row, 'CRITICAL'),
					thisWeek: getField(row, 'HIGH'),
					keepAnEye: getField(row, 'MEDIUM_LOW', 'LOW_MEDIUM'),
					codesFlagged: getField(row, 'VIN_COUNT'),
				};

				dispatch.fleetManager.setTaskCounts(taskCounts);
			} catch (error: any) {
				logApiError('fetchTaskCountsAsync (vc_fleet_manager_tasks_counts_2)', error);
				dispatch.fleetManager.setTaskCountsError(
					error?.response?.data?.message ||
						error?.message ||
						'Failed to fetch task counts',
				);
			}
		},

		async fetchAgentsListAsync() {
			dispatch.fleetManager.setAgentsListLoading(true);
			try {
				const { data } = await httpRest.get(
					'vc_fleet_manager_list_of_agent',
					configHeadersApiRest(),
				);
				const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
				const list: string[] = row && Array.isArray(row.agent_name) ? row.agent_name : [];

				if (list.length === 0) {
					dispatch.fleetManager.setAgentsList(['All Agents']);
					return;
				}

				const normalized = list.includes('All Agents') ? list : ['All Agents', ...list];
				dispatch.fleetManager.setAgentsList(normalized);
			} catch (error: any) {
				logApiError('fetchAgentsListAsync (vc_fleet_manager_list_of_agent)', error);
				dispatch.fleetManager.setAgentsListError(
					error?.response?.data?.message ||
						error?.message ||
						'Failed to fetch agent list',
				);
				dispatch.fleetManager.setAgentsList(['All Agents']);
			}
		},

		async fetchTasksAsync(filterParams: TaskFilterParams) {
			dispatch.fleetManager.setTasksLoading(true);
			dispatch.fleetManager.setLastTaskFilter(filterParams);

			try {
				const query: Record<string, any> = {};

				query.Vin =
					filterParams.Vin && filterParams.Vin !== 'All Vins'
						? filterParams.Vin
						: 'All Vins';

				query.fleet_id =
					filterParams.fleet_id && filterParams.fleet_id !== 'All Fleets'
						? filterParams.fleet_id
						: 'All Fleets';

				query.Agent_name =
					filterParams.Agent_name && filterParams.Agent_name !== 'All Agents'
						? filterParams.Agent_name
						: 'All Agents';

				if (filterParams.organisation_id) {
					query.organisation_id = filterParams.organisation_id;
				}

				const nowDate = new Date();
				const defaultEndDate = nowDate.toISOString().split('.')[0];
				const sevenDaysAgo = new Date(nowDate);
				sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
				sevenDaysAgo.setHours(0, 0, 0, 0);
				const defaultStartDate = sevenDaysAgo.toISOString().split('.')[0];
				query.start_date = filterParams.start_date ?? defaultStartDate;
				query.end_date = filterParams.end_date ?? defaultEndDate;

				query.coaching_priority = filterParams.coaching_priority ?? 'CRITICAL';
				query.status = filterParams.status ?? 'All Status';


				const config = { ...configHeadersApiRest(), params: query };
				const { data } = await httpRest.get('vc_fleet_manager_total_tasks_2', config);
				const tasks: Task[] = Array.isArray(data) ? data : [];

				dispatch.fleetManager.setTasks(tasks);
			} catch (error: any) {
				logApiError('fetchTasksAsync (vc_fleet_manager_total_tasks_2)', error);
				
				// Clear stale data on error
				dispatch.fleetManager.setTasksError(
					error?.response?.data?.message || 
					error?.message || 
					'Failed to fetch tasks. Check API endpoint and backend service.',
				);
			}
		},

		async updateStatusAsync(params: {
			correlationId: string;
			status: 'Open' | 'In_progress' | 'Completed';
		}) {
			dispatch.fleetManager.patchTaskStatus({
				correlationId: params.correlationId,
				status:
					params.status === 'Completed'
						? 'completed'
						: params.status === 'In_progress'
						? 'in_progress'
						: 'open',
			});

			try {
				const ingestionBaseUrl =
					process.env.REACT_APP_SERVICE_URL_INGESTION ?? 'https://prod.ravity.io/';
				const ingestionId = process.env.REACT_APP_INGESTIONID_LLM ?? '';
				const ingestionSecret = process.env.REACT_APP_INGESTIONSECRET_LLM ?? '';

				const body = {
					action: 'Update_status',
					key: params.correlationId,
					status: params.status,
				};

				await axios.post(`${ingestionBaseUrl}ingestion/dataIngestion`, body, {
					headers: {
						'Content-Type': 'application/json',
						ingestionId,
						ingestionSecret,
					},
				});
			} catch (error: any) {
				console.error(
					'[fleetManager] Status update failed:',
					error?.response?.data ?? error?.message,
				);
			}
		},

		async fetchAiAnalysisAsync(params: { correlationId: string }) {
			dispatch.fleetManager.setAiAnalysisLoading(true);
			try {
				const config = {
					...configHeadersApiRest(),
					params: {
						correlationId: params.correlationId,
					},
				};
				const { data } = await httpRest.get('vc_fleet_manager_AI_Analaysis_2', config);
				const result: AIAnalysisResult | null =
					Array.isArray(data) && data.length > 0
						? data[0]
						: data && typeof data === 'object' && !Array.isArray(data)
						? (data as AIAnalysisResult)
						: null;
				dispatch.fleetManager.setAiAnalysis(result);
			} catch (error: any) {
				logApiError('fetchAiAnalysisAsync (vc_fleet_manager_AI_Analaysis_2)', error);
				dispatch.fleetManager.setAiAnalysisError(
					error?.response?.data?.message ||
						error?.message ||
						'Failed to fetch AI analysis',
				);
			}
		},
	}),
});